import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import {
    checkModalValidationsForConfig, isValidEntry, PATTERNS
} from "../../../util/Validations";
import {ADTF_ASSET_TYPE, CONFIG_TYPE, INSTALLATION_TYPES, STOW_STATION_TIERS} from "../../../util/Constants";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import Select, {SelectOption} from "@amzn/meridian/select";
import {getADTFSorterConfigKey, getSorterConfigLocationStringForm} from "../../../util/Util";
import {FEATURES, FeatureUtility} from "../../../util/FeatureUtility";
import {getLocalizedMessage} from "../../../util/Localization";

const AddAdtfSorterConfigurationModal = (props) => {

    const {intl} = props;
    const {formatMessage} = intl;
    const {mac} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext)

    // Temporarily added MISS in order to maintain parity between backend and frontend.
    // Should be removed in long-term, refer: https://t.corp.amazon.com/D219308494
    const ADTF_INSTALLATION_TYPES = {
        ...INSTALLATION_TYPES,
        MISS: {
            label: "Miss",
            value: "miss"
        }
    }

    // Stow Station diversion (Dual Stow Operation) is behind a feature flag; hide the option until enabled.
    if (!FeatureUtility.getFeatureStatus(FEATURES.IS_STOW_STATION_INSTALLATION_TYPE_ENABLED)) {
        delete ADTF_INSTALLATION_TYPES.STOW_STATION;
    }

    // Visual controls
    const {open, setOpen} = props
    const [show, setShow] = useState(false)
    const [edit, setEdit] = useState(false)
    const [showLocation, setShowLocation] = useState(true)

    // Values
    const [locations, setLocations] = useState("")
    // For stow station, the location is entered as a cluster + tier pair (e.g. "A#2")
    // instead of a free-form text box. These drive the `locations` string when type is stow station.
    const [cluster, setCluster] = useState("")
    const [tier, setTier] = useState("")
    const [type, setType] = useState("")
    const [destinationId, setDestinationId] = useState("")
    const [sorterId, setSorterId] = useState("")

    // Error
    const [locationsErr, setLocationsErr] = useState(false)
    const [typeErr, setTypeErr] = useState(false)
    const [destinationIdErr, setDestinationIdErr] = useState(false)
    const [sorterIdErr, setSorterIdErr] = useState(false)

    const onCloseModal = () => {
        setShow(false);
        setOpen(false)
        resetInputState();
    }

    const isStowStation = type === INSTALLATION_TYPES.STOW_STATION.value;

    // For stow station, the location string is a single cluster#tier pair built from the
    // cluster text box and tier dropdown. For all other types it is the free-form text box.
    const effectiveLocations = isStowStation
        ? (cluster && tier ? `${cluster}#${tier}` : "")
        : locations;

    const modalSorterConfig = {
        destinationId: destinationId,
        sorterId: sorterId,
        installation: {
            type: type,
            locations: effectiveLocations
        }
    }

    /* Filling a map: sorterConfigMap with destinationId as key in case of an update of existing asset */
    const sorterConfigMap = {}
    getDraftData({stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION, subKey: mac}).forEach(value => {
        const key = getADTFSorterConfigKey(value.sorterId, value.destinationId, value.installation.type);
        sorterConfigMap[key] = {
            locations: value.installation.locations,
            sorterId: value.sorterId,
            type: value.type
        }
    });

    const onClickConfirm = (modalSorterConfig, mac) => {
        if (checkModalValidationsForConfig(modalSorterConfig, mac, ADTF_ASSET_TYPE)) {
            setConfigData(modalSorterConfig, mac);
            setOpen(false)
            resetInputState();
            setShow(false);
        } else {
            setShow(true)
        }
    }

    function resetInputState() {
        setDestinationId("");
        setSorterId("");
        setDestinationIdErr(false);
        setSorterIdErr(false);
        setType("");
        setTypeErr(false);
        resetConfigDetails();
        resetConfigErrorDetails();
    }

    const resetConfigDetails = () => {
        setLocations("");
        setCluster("");
        setTier("");
        setShowLocation(true);
    }

    const resetConfigErrorDetails = () => {
        setTypeErr(false);
        setLocationsErr(false);
    }

    const processType = (type) => {
        if (type === 'miss' || type === 'jackpot' || type === 'ov' || type === INSTALLATION_TYPES.FRAGILE.value) {
            setShowLocation(false);
        } else {
            setShowLocation(true);
        }
    }

    const prefill = () => {
        const key = getADTFSorterConfigKey(sorterId, destinationId, type);
        if (sorterConfigMap.hasOwnProperty(key)) {
            setEdit(true);
            resetConfigErrorDetails();
            const locationString = getSorterConfigLocationStringForm(sorterConfigMap[key].locations);
            setLocations(locationString);
            // For stow station, split the "cluster#tier" pair back into the dropdown fields.
            if (type === INSTALLATION_TYPES.STOW_STATION.value) {
                const [prefillCluster, prefillTier] = (locationString || "").split("#");
                setCluster(prefillCluster || "");
                setTier(prefillTier || "");
            }
        } else if (edit) {
            setEdit(false);
            resetConfigDetails()
        }
    }

    const setConfigData = (modalSorterConfig, mac) => {
        if (modalSorterConfig.installation.locations) {
            modalSorterConfig.installation.locations = modalSorterConfig.installation.locations
                .replace(/\s/g, '').split(';').filter(Boolean);
        } else {
            modalSorterConfig.installation.locations = [];
        }

        /* sorter config are stored and passed as map with destinationId as key */
        if(edit) {
            editInstance({
                stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
                subKey: mac,
                instance: modalSorterConfig,
                keyComparator: (config1, config2) => (
                    getADTFSorterConfigKey(config1.sorterId, config1.destinationId, config1.installation.type) ===
                    getADTFSorterConfigKey(config2.sorterId, config2.destinationId, config2.installation.type)
                ),
                valueComparator: (config1, config2) => (
                    JSON.stringify(config1.sorterId) === JSON.stringify(config2.sorterId) &&
                    JSON.stringify(config1.installation.locations) === JSON.stringify(config2.installation.locations)
                )
            })
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ADTF_SORTER_DESTINATION,
                subKey: mac,
                instance: modalSorterConfig
            })
        }

    }

    return (
        <Row>
            <Modal
                title={formatMessage({id: "sorterconfig.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b> <FormattedMessage id="modal.addheading"
                                              defaultMessage="To edit an existing configuration, enter the Destination Id."/>
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.destinationid" defaultMessage="Destination ID"/>
                        </Text>
                        <Input id="destination-id-input"
                               value={destinationId}
                               onChange={setDestinationId}
                               onBlur={() => {
                                   prefill();
                               }}
                               error={destinationIdErr}/>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.sorterId" defaultMessage="Sorter ID"/>
                        </Text>
                        <Input id="sorter-id-input"
                               value={sorterId}
                               onChange={setSorterId}
                               onBlur={() => {
                                   prefill();
                               }}
                               error={sorterIdErr}/>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            <FormattedMessage id="modal.installationtype"
                                              defaultMessage="Installation Type"/>
                        </Text>
                        <Select id="type-input"
                            value={type}
                            onChange={setType}
                            onBlur={() => {
                                processType(type);
                                prefill();
                            }}
                            placeholder={<FormattedMessage id="modal.enter_value"
                                               defaultMessage="Enter value..."/>}
                            width="100%"
                            error={typeErr}>
                            {Object.values(ADTF_INSTALLATION_TYPES).map(({label, value}, index) =>
                                <SelectOption key={index} value={value} label={label} />
                            )}
                        </Select>
                    </Column>
                    { showLocation && (isStowStation ? (
                        <Row spacing="large" alignmentVertical="top" widths={["fill", "fill"]}>
                            <Column spacing="200">
                                <Text tag="label" type="b200" color="primary" htmlFor="cluster-input">
                                    {getLocalizedMessage({intl, stringId: "modal.cluster"})}
                                </Text>
                                <Input id="cluster-input"
                                       value={cluster}
                                       onChange={setCluster}
                                       onBlur={() => setLocationsErr(!isValidEntry(effectiveLocations, PATTERNS.STOW_STATION_LOCATION))}
                                       error={locationsErr}/>
                            </Column>
                            <Column spacing="200">
                                <Text tag="label" type="b200" color="primary" htmlFor="tier-input">
                                    {getLocalizedMessage({intl, stringId: "modal.tier"})}
                                </Text>
                                <Select id="tier-input"
                                        value={tier}
                                        onChange={setTier}
                                        onBlur={() => setLocationsErr(!isValidEntry(effectiveLocations, PATTERNS.STOW_STATION_LOCATION))}
                                        placeholder={getLocalizedMessage({intl, stringId: "modal.enter_value"})}
                                        width="100%"
                                        error={locationsErr}>
                                    {STOW_STATION_TIERS.map(({label, value}) =>
                                        <SelectOption key={value} value={value} label={label}/>
                                    )}
                                </Select>
                            </Column>
                        </Row>
                    ) : (
                        <Column spacing="200">
                            <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                                <FormattedMessage id="modal.location" defaultMessage="Locations"/>
                            </Text>
                            <Input id="location-input"
                                   value={locations}
                                   onChange={setLocations}
                                   onBlur={() => {
                                       setLocationsErr(!isValidEntry(locations, PATTERNS.LOCATION))
                                   }}
                                   error={locationsErr}
                                   helperText="Cluster can have values like: A, B, C..."/>
                        </Column>
                    )) }
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkModalValidationsForConfig(modalSorterConfig, mac, ADTF_ASSET_TYPE) &&
                            show &&
                            <Alert type="warning"
                                   size="small">
                                <FormattedMessage id="alert.missingfields"
                                                  defaultMessage="Please enter all fields in correct format!"/>
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(modalSorterConfig, mac)}>
                            { edit ? <FormattedMessage id="modal.confirmUpdate" defaultMessage="Confirm update"/>
                                : <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddAdtfSorterConfigurationModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtfdevice/modal/AddAdtfSorterConfigurationModal.js